import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, UserCheck, UserX, Clock, Thermometer, Droplet, SprayCan, AlertTriangle, FileText, ClipboardCheck, ChefHat, ShoppingCart, Shield } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrador" },
  { value: "supervisor", label: "Supervisor" },
  { value: "worker", label: "Treballador" },
  { value: "auditor", label: "Auditor" },
];

const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700",
  supervisor: "bg-blue-100 text-blue-700",
  worker: "bg-green-100 text-green-700",
  auditor: "bg-purple-100 text-purple-700",
};

// Modules assignable to workers
const ASSIGNABLE_MODULES = [
  { code: "time_control", label: "Control Horari", icon: Clock, always: true },
  { code: "temperatures", label: "Temperatures", icon: Thermometer },
  { code: "chlorine", label: "Control de Clor", icon: Droplet },
  { code: "cleaning", label: "Neteja", icon: SprayCan },
  { code: "incidents", label: "Incidencies", icon: AlertTriangle },
  { code: "documents", label: "Documents", icon: FileText },
  { code: "verifications", label: "Verificacions", icon: ClipboardCheck },
  { code: "recipe_sheets", label: "Fitxes Tecniques", icon: ChefHat },
  { code: "ingredients", label: "Ingredients", icon: ShoppingCart },
  { code: "appcc", label: "APPCC", icon: Shield },
];

function MemberForm({ member, venueId, onSave, onCancel }) {
  const defaultPermissions = ["time_control"];
  const [form, setForm] = useState(member ? {
    ...member,
    permissions: member.permissions || defaultPermissions,
  } : {
    user_email: "", user_name: "", role: "worker", position: "", phone: "", active: true,
    permissions: [...defaultPermissions],
  });
  const [saving, setSaving] = useState(false);

  const isValid = form.user_email && form.user_name && form.role;
  const showPermissions = form.role === "worker";

  const togglePermission = (code) => {
    const mod = ASSIGNABLE_MODULES.find(m => m.code === code);
    if (mod?.always) return; // Can't remove mandatory modules
    const perms = form.permissions || [];
    if (perms.includes(code)) {
      setForm(f => ({ ...f, permissions: perms.filter(p => p !== code) }));
    } else {
      setForm(f => ({ ...f, permissions: [...perms, code] }));
    }
  };

  const selectAll = () => {
    setForm(f => ({ ...f, permissions: ASSIGNABLE_MODULES.map(m => m.code) }));
  };

  const selectNone = () => {
    setForm(f => ({ ...f, permissions: ASSIGNABLE_MODULES.filter(m => m.always).map(m => m.code) }));
  };

  const save = async () => {
    if (!isValid) return;
    setSaving(true);
    const data = { ...form };
    // Only store permissions for workers
    if (data.role !== "worker") {
      delete data.permissions;
    }
    if (member?.id) {
      await hosys.entities.VenueMember.update(member.id, data);
    } else {
      await hosys.entities.VenueMember.create({ ...data, venue_id: venueId });
    }
    setSaving(false);
    onSave();
  };

  return (
    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 space-y-3" data-testid="member-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Nom *</label>
          <Input
            placeholder="Nom complet"
            value={form.user_name}
            onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))}
            data-testid="member-name-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Email *</label>
          <Input
            type="email"
            placeholder="email@exemple.com"
            value={form.user_email}
            onChange={e => setForm(f => ({ ...f, user_email: e.target.value }))}
            disabled={!!member?.id}
            data-testid="member-email-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Rol *</label>
          <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="member-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Carrec / Posicio</label>
          <Input
            placeholder="Ex: Cap de cuina, Cambrer..."
            value={form.position || ""}
            onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Telefon</label>
          <Input
            type="tel"
            placeholder="6XX XXX XXX"
            value={form.phone || ""}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">DNI / NIE</label>
          <Input
            placeholder="12345678A"
            value={form.dni_nie || ""}
            onChange={e => setForm(f => ({ ...f, dni_nie: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">NAF (Seg. Social)</label>
          <Input
            placeholder="431027440559"
            value={form.naf || ""}
            onChange={e => setForm(f => ({ ...f, naf: e.target.value }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">PIN kiosk (4 digits)</label>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={4}
            placeholder="1234"
            value={form.pin || ""}
            onChange={e => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 4);
              setForm(f => ({ ...f, pin: v }));
            }}
          />
          <p className="text-[10px] text-gray-400 mt-1">Opcional. Si no s'assigna, el treballador no necessita PIN.</p>
        </div>
      </div>

      {/* Permissions section - only for workers */}
      {showPermissions && (
        <div className="space-y-2 pt-2 border-t border-gray-200" data-testid="permissions-section">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Permisos d'acces als moduls
            </label>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-[10px] text-[#7B2D3F] font-medium hover:underline">Tots</button>
              <span className="text-gray-300">|</span>
              <button onClick={selectNone} className="text-[10px] text-gray-500 font-medium hover:underline">Cap</button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {ASSIGNABLE_MODULES.map(mod => {
              const isChecked = (form.permissions || []).includes(mod.code);
              const Icon = mod.icon;
              return (
                <button
                  key={mod.code}
                  type="button"
                  onClick={() => togglePermission(mod.code)}
                  disabled={mod.always}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all text-xs font-medium ${
                    isChecked
                      ? "border-[#7B2D3F] bg-[#7B2D3F]/5 text-[#7B2D3F]"
                      : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  } ${mod.always ? "cursor-default opacity-80" : "cursor-pointer"}`}
                  data-testid={`perm-${mod.code}`}
                >
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isChecked ? "bg-[#7B2D3F]/10" : "bg-gray-100"
                  }`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <span className="truncate">{mod.label}</span>
                  {mod.always && <span className="text-[8px] text-gray-400 ml-auto">Obligat.</span>}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-400">
            Selecciona els moduls als quals el treballador tindra acces. "Control Horari" es obligatori per a tots els treballadors.
          </p>
        </div>
      )}

      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel-lar</Button>
        <Button
          size="sm"
          disabled={!isValid || saving}
          onClick={save}
          className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white"
          data-testid="member-save-btn"
        >
          {saving ? "Guardant..." : member?.id ? "Actualitzar" : "Afegir membre"}
        </Button>
      </div>
    </div>
  );
}

function PermissionBadges({ permissions }) {
  if (!permissions || permissions.length === 0) return null;
  const modules = ASSIGNABLE_MODULES.filter(m => permissions.includes(m.code) && !m.always);
  if (modules.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {modules.map(mod => {
        const Icon = mod.icon;
        return (
          <span key={mod.code} className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded-md">
            <Icon className="w-2.5 h-2.5" />
            {mod.label}
          </span>
        );
      })}
    </div>
  );
}

export default function StaffManager({ venueId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => {
    if (venueId) load();
  }, [venueId]);

  const load = async () => {
    setLoading(true);
    const mems = await hosys.entities.VenueMember.filter({ venue_id: venueId });
    setMembers(mems.sort((a, b) => (a.user_name || "").localeCompare(b.user_name || "")));
    setLoading(false);
  };

  const toggleActive = async (m) => {
    await hosys.entities.VenueMember.update(m.id, { active: !m.active });
    load();
  };

  const deleteMember = async (m) => {
    await hosys.entities.VenueMember.delete(m.id);
    load();
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingMember(null);
    load();
  };

  if (loading) return <div className="py-8 text-center text-gray-400 text-sm">Carregant personal...</div>;

  return (
    <div className="space-y-4" data-testid="staff-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">Personal del local</h3>
          <p className="text-xs text-gray-400 mt-0.5">{members.filter(m => m.active).length} actius - {members.length} total</p>
        </div>
        {!showForm && !editingMember && (
          <Button
            size="sm"
            onClick={() => setShowForm(true)}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white gap-2"
            data-testid="add-member-btn"
          >
            <Plus className="w-4 h-4" /> Afegir persona
          </Button>
        )}
      </div>

      {(showForm && !editingMember) && (
        <MemberForm
          venueId={venueId}
          onSave={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {members.length === 0 && !showForm ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          No hi ha cap membre configurat. Afegeix el primer treballador.
        </div>
      ) : (
        <div className="space-y-2">
          {members.map(m => (
            <div key={m.id}>
              {editingMember?.id === m.id ? (
                <MemberForm
                  member={m}
                  venueId={venueId}
                  onSave={handleSaved}
                  onCancel={() => setEditingMember(null)}
                />
              ) : (
                <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${m.active ? "bg-white border-gray-100" : "bg-gray-50 border-gray-100 opacity-60"}`}>
                  <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#7B2D3F] font-bold text-sm">{(m.user_name || m.user_email)[0]?.toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800 text-sm">{m.user_name || m.user_email}</span>
                      <Badge className={`text-[10px] px-2 py-0.5 ${ROLE_COLORS[m.role] || "bg-gray-100 text-gray-600"}`}>
                        {ROLE_OPTIONS.find(r => r.value === m.role)?.label || m.role}
                      </Badge>
                      {!m.active && <Badge className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500">Inactiu</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {m.position && <span className="text-[11px] text-gray-400">{m.position}</span>}
                      {m.phone && <span className="text-[11px] text-gray-400">{m.phone}</span>}
                      {!m.position && !m.phone && <span className="text-[11px] text-gray-400">{m.user_email}</span>}
                    </div>
                    {m.role === "worker" && <PermissionBadges permissions={m.permissions} />}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditingMember(m)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      title="Editar"
                      data-testid={`edit-member-${m.user_email}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => toggleActive(m)}
                      className={`p-1.5 rounded-lg transition-colors ${m.active ? "text-gray-400 hover:text-amber-500 hover:bg-amber-50" : "text-gray-400 hover:text-green-500 hover:bg-green-50"}`}
                      title={m.active ? "Desactivar" : "Activar"}
                    >
                      {m.active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => deleteMember(m)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
